import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import moment from 'moment';
import toast from 'react-hot-toast';

import httpClient from 'utils/httpClient';
import { TimesheetsTable } from 'views/ProjectManagment/components/ProjectTimesheetSection';

const formatDate = (date) => {
  if (!date) {
    return '-';
  }

  return moment(date).format('MMM DD, YYYY');
};

const formatDateTime = (dateTime) => {
  if (!dateTime) {
    return '-';
  }

  return moment(dateTime).format('MMM DD, YYYY, h:mm A');
};

const TimesheetSection = ({ projectId }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const isMountedRef = useRef(false);

  const fetchTimesheets = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    if (!projectId) {
      setTimesheets([]);
      setStats(null);
      setIsFetching(false);
      return;
    }

    setIsFetching(true);

    try {
      const response = await httpClient.get(
        '/api/v1/project-management/timesheets/',
        {
          params: {
            project: projectId,
          },
        }
      );

      const {
        status,
        message,
        data,
        results,
        items,
        stats: responseStats,
      } = response ?? {};

      if (status === false) {
        throw new Error(message || 'Failed to fetch timesheets');
      }

      const responseData = data || results || response || {};
      const timesheetResults =
        responseData.items ||
        items ||
        responseData.results ||
        responseData ||
        [];
      const timesheetStats = responseData.stats || responseStats || null;

      if (isMountedRef.current) {
        setTimesheets(Array.isArray(timesheetResults) ? timesheetResults : []);
        setStats(timesheetStats);
      }
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      toast.error(error?.message || 'Failed to fetch timesheets');

      if (isMountedRef.current) {
        setTimesheets([]);
        setStats(null);
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchTimesheets();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchTimesheets]);

  const hasTimesheets = timesheets.length > 0;
  const totalTimesheets = Number.isFinite(Number(stats?.total_timesheets))
    ? Number(stats.total_timesheets)
    : timesheets.length;

  return (
    <Paper sx={{ mb: 3, borderRadius: 2 }}>
      {isFetching && (
        <LinearProgress
          aria-label='Loading timesheets'
          sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
        />
      )}

      <Box sx={{ p: 3, pb: hasTimesheets ? 3 : 6 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 600 }}>
              Timesheets ({totalTimesheets})
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Review work logs submitted against this SOW.
            </Typography>
          </Box>
        </Box>

        {hasTimesheets ? (
          <TimesheetsTable
            timesheets={timesheets}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant='body1' color='text.secondary'>
              {projectId
                ? 'No timesheets found for this SOW.'
                : 'Project information is required to load timesheets.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default TimesheetSection;
